import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button";
import { ArrowLeft, FileText } from "lucide-react";
import type { MapaAtasProjeto } from "../../../../types/database";
import { ExportLanguageDialog, type ExportLanguage } from "./ExportLanguageDialog";

interface MapaAtasHeaderProps {
  projeto: MapaAtasProjeto;
  onExportPDF?: (language: ExportLanguage) => void;
}

export function MapaAtasHeader({ projeto, onExportPDF }: MapaAtasHeaderProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [showExportDialog, setShowExportDialog] = useState(false);

  const handleExport = (language: ExportLanguage) => {
    if (onExportPDF) {
      onExportPDF(language);
    }
  };

  return (
    <>
      <div className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Botão voltar */}
            <button
              onClick={() => navigate(`/projects/${projeto.id}/meetings`)}
              className="p-2 hover:bg-muted rounded-md transition-colors flex-shrink-0"
              aria-label={t('mapaAtas.back_to_meetings')}
            >
              <ArrowLeft size={18} className="text-muted-foreground" />
            </button>

            {/* Info principal */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-semibold text-foreground truncate">
                  {t('mapaAtas.title')}
                </h1>
                <span className="text-muted-foreground hidden sm:inline">•</span>
                <span className="text-sm text-muted-foreground truncate hidden sm:inline">{projeto.nome}</span>
              </div>

              {/* Metadata inline */}
              {projeto.endereco && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate max-w-64">{projeto.endereco}</span>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => setShowExportDialog(true)}
                variant="default"
                size="sm"
                className="gap-2"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">{t('mapaAtas.export_pdf')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ExportLanguageDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        onExport={handleExport}
      />
    </>
  );
}
